import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BrainCircuit, Search, ShieldCheck, HeartHandshake, Globe2, ArrowRight,
  BarChart3, BookOpen, Megaphone, Users, Newspaper, Trophy, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import SectionTitle from "@/components/SectionTitle";
import SEO from "@/components/SEO";
import InvestigativeScene from "@/components/InvestigativeScene";

const HERO_BG = "https://images.unsplash.com/photo-1704093240724-891c11815f42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxnbG9iYWwlMjBuZXR3b3JrJTIwZGF0YSUyMGFic3RyYWN0JTIwZGFya3xlbnwwfHx8YmxhY2t8MTc4MzA3NDE2NXww&ixlib=rb-4.1.0&q=85";
const JUSTICE_BG = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxzY2FsZXMlMjBvZiUyMGp1c3RpY2V8ZW58MHx8fHwxNzgzMDc0MTc3fDA&ixlib=rb-4.1.0&q=85";
const INSTITUTE_BG = "https://images.unsplash.com/photo-1727221167365-d365e63e7ebc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHx1bml0ZWQlMjBuYXRpb25zJTIwYnVpbGRpbmclMjBtb2Rlcm58ZW58MHx8fHwxNzgzMDc0MTY4fDA&ixlib=rb-4.1.0&q=85";
const CYBER_BG = "https://images.unsplash.com/photo-1653119574903-ae9c3204bc35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxjeWJlciUyMHNlY3VyaXR5JTIwYWJzdHJhY3R8ZW58MHx8fGJsYWNrfDE3ODMwNzQxNzd8MA&ixlib=rb-4.1.0&q=85";

const AI_FEATURES = [
  { icon: BrainCircuit, title: "AI Crime Assistant", desc: "Ask about laws, scams, cybercrime, trafficking, laundering.", to: "/ai-assistant" },
  { icon: Search, title: "Scam Detection", desc: "Upload messages, emails or screenshots for AI risk analysis.", to: "/scam-detection" },
  { icon: BarChart3, title: "Intelligence Dashboard", desc: "Global crime trends, heatmaps and country comparisons.", to: "/dashboard" },
  { icon: BookOpen, title: "Research Library", desc: "Peer-reviewed papers, briefs and annual reports.", to: "/research" },
];

const PARTNERS = ["UNODC", "INTERPOL", "EUROPOL", "OECD", "FATF", "Council of Europe", "OSCE", "IOM"];

const TESTIMONIALS = [
  { name: "Prof. Andrea Rossi", role: "Sapienza University of Rome", quote: "IOCRI's open datasets have transformed how we teach transnational crime to policy graduates." },
  { name: "Amara Okonkwo", role: "Investigative Journalist, Lagos", quote: "The AI Assistant is my first call before drafting any complex financial-crime feature." },
  { name: "Dept. of Cybercrime, Regional Police", role: "Central Asia", quote: "The Scam Detection tool helped us triage over 4,000 citizen reports last quarter." },
];

const CAMPAIGNS = [
  { title: "#StopTheDeepfake", tag: "Cyber Safety", color: "from-gold/20" },
  { title: "Behind Closed Borders", tag: "Anti-Trafficking", color: "from-red-900/30" },
  { title: "Money Trails", tag: "AML Awareness", color: "from-emerald-900/20" },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.items || [])).catch(() => {});
  }, []);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success("Welcome to the IOCRI Briefing."); setEmail(""); }
    catch { toast.error("Subscription failed."); }
  };

  return (
    <div>
      <SEO title="Empowering the World Against Organized Crime" description="Research. Education. AI. Victim Support. Global Collaboration. Independent institute against organized crime, cybercrime, human trafficking and money laundering." path="/" image="default" />
      {/* HERO — Crimson Intelligence 4D scene */}
      <section className="relative min-h-[92vh] hero-radial overflow-hidden" data-testid="home-hero">
        <InvestigativeScene />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-obsidian pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="max-w-5xl">
            <div className="inline-flex items-center gap-2 border border-white/15 rounded-sm px-3 py-1.5 text-[10px] font-mono text-white/70 tracking-[0.35em]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold" /> IOCRI · OPEN-SOURCE RESEARCH NETWORK
            </div>
            <h1 className="font-display font-medium text-5xl md:text-7xl mt-8 leading-[0.95] text-balance text-white">
              BEHIND EVERY CRIME<br/>
              IS A <span style={{ color: "#C1121F" }}>NETWORK.</span>
            </h1>
            <div className="mt-6 font-mono text-sm tracking-[0.3em] text-gold">WE STUDY THE CONNECTIONS.</div>
            <p className="mt-6 text-white/70 text-lg max-w-2xl">
              Independent research, education, analytical tools, and public resources focused on transnational organized crime and emerging global threats.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <Link to="/research"><Button data-testid="hero-research-btn" className="rounded-sm bg-gold text-black hover:bg-gold-light h-12 px-7 font-mono text-xs tracking-widest">EXPLORE RESEARCH <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/dashboard"><Button data-testid="hero-trace-btn" variant="outline" className="rounded-sm h-12 px-7 border-white/20 bg-transparent hover:bg-white/5 text-white font-mono text-xs tracking-widest">TRACE THE NETWORK</Button></Link>
              <Link to="/scam-detection"><Button data-testid="hero-scam-btn" variant="outline" className="rounded-sm h-12 px-7 bg-transparent font-mono text-xs tracking-widest" style={{ borderColor: "rgba(193,18,31,0.6)", color: "#FF3B30" }}>ANALYZE A SCAM</Button></Link>
              <Link to="/ai-assistant" data-testid="hero-ai-link" className="link-underline text-white/60 hover:text-gold font-mono text-xs tracking-widest ml-2">ASK IOCRI AI →</Link>
            </div>
          </motion.div>

          {stats && (
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
              {[
                { k: "Cybercrime cost", v: `$${stats.cybercrime_cost_usd_trn}T`, s: "annual, 2026" },
                { k: "Trafficking victims", v: stats.trafficking_victims_detected.toLocaleString(), s: "detected globally" },
                { k: "Laundered / global GDP", v: `${stats.money_laundered_pct_gdp}%`, s: "UNODC estimate" },
                { k: "Drug users", v: `${stats.drug_users_global_mn}M`, s: "worldwide, 2024" },
              ].map((s, i) => (
                <div key={i} className="bg-obsidian-soft p-6" data-testid={`hero-stat-${i}`}>
                  <div className="text-[10px] font-mono text-gold tracking-[0.3em]">{s.k.toUpperCase()}</div>
                  <div className="font-display text-3xl md:text-4xl mt-3 text-white">{s.v}</div>
                  <div className="text-white/40 text-xs mt-1 font-mono">{s.s}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ABOUT + MISSION */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-14 items-center" data-testid="home-about">
        <div>
          <SectionTitle eyebrow="ABOUT IOCRI" title="An independent institute where research meets justice." subtitle="We combine investigative rigour with modern AI to inform policy, empower victims, and strengthen the global response to organized crime." />
          <div className="mt-8 space-y-4">
            {[
              ["Global Mission", "Reduce the harm of transnational organized crime through open knowledge and technology."],
              ["Independence", "Non-partisan, non-governmental, and privately funded — accountable to the public."],
              ["Human-first", "Every publication centres the dignity and safety of victims and communities."],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-4 border-l border-gold/40 pl-5 py-2">
                <div>
                  <div className="text-gold font-medium">{t}</div>
                  <div className="text-white/60">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <img src={INSTITUTE_BG} alt="Institute" className="rounded-2xl border border-white/5" />
          <div className="absolute -bottom-6 -left-6 glass p-6 rounded-2xl w-64 hidden md:block">
            <div className="font-mono text-xs text-gold tracking-widest">EST. 2026</div>
            <div className="font-display text-2xl mt-2">42 Countries</div>
            <div className="text-white/60 text-sm">Active research partners</div>
          </div>
        </div>
      </section>

      {/* AI FEATURES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="home-ai-features">
        <SectionTitle eyebrow="AI FEATURES" title="Purpose-built AI for public safety." subtitle="Educational, transparent, and always paired with human expertise." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {AI_FEATURES.map((f) => (
            <Link key={f.title} to={f.to} data-testid={`ai-feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <Card className="bg-obsidian-soft border-white/5 rounded-2xl h-full hover:border-gold/40 transition-all group">
                <CardContent className="p-6">
                  <div className="h-11 w-11 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <f.icon className="h-5 w-5 text-gold" />
                  </div>
                  <div className="mt-5 font-display text-xl text-white">{f.title}</div>
                  <div className="mt-2 text-white/60 text-sm">{f.desc}</div>
                  <div className="mt-6 text-gold text-xs font-mono tracking-widest opacity-70 group-hover:opacity-100">EXPLORE →</div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* CRIME STATS chart bento */}
      <section className="relative py-24" data-testid="home-stats">
        <div className="absolute inset-0 opacity-20"><img src={CYBER_BG} alt="" className="w-full h-full object-cover" /></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <SectionTitle eyebrow="CRIME STATISTICS" title="The scale of the problem, in numbers." />
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {stats?.regions?.map((r) => (
              <div key={r.region} className="glass rounded-2xl p-6" data-testid={`region-card-${r.region.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display text-2xl">{r.region}</div>
                  <Globe2 className="h-5 w-5 text-gold" />
                </div>
                <div className="mt-4 font-mono text-4xl text-gold-gradient">{r.score.toFixed(2)}</div>
                <div className="text-xs text-white/50 mt-1">Global Organized Crime Index 2023</div>
                <div className="mt-4 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gold" style={{ width: `${(r.score / 10) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6"><Link to="/dashboard"><Button variant="outline" className="rounded-full border-gold/50 text-gold hover:bg-gold/10 bg-transparent" data-testid="view-dashboard-btn">Open Intelligence Dashboard <ArrowRight className="ml-2 h-4 w-4" /></Button></Link></div>
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="home-news">
        <div className="flex items-end justify-between">
          <SectionTitle eyebrow="LATEST GLOBAL CRIME NEWS" title="Curated from UNODC, INTERPOL, Europol and IOCRI." />
          <Newspaper className="h-6 w-6 text-gold hidden md:block" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {news.slice(0, 6).map((n) => (
            <a key={n.id} href={n.url} target="_blank" rel="noreferrer" data-testid={`news-item-${n.id}`} className="group">
              <Card className="bg-obsidian-soft border-white/5 rounded-2xl h-full hover:border-gold/40 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-gold">{n.source}</span>
                    <span className="text-white/40">{n.date}</span>
                  </div>
                  <div className="font-display text-xl mt-4 leading-snug group-hover:text-gold transition-colors">{n.title}</div>
                  <div className="mt-3 text-white/60 text-sm">{n.summary}</div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </section>

      {/* VICTIM SUPPORT / AWARENESS / VOLUNTEER bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="home-bento">
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/victim-support" className="md:col-span-2 relative overflow-hidden rounded-2xl border border-white/5 bg-obsidian-soft p-10 min-h-[280px] group" data-testid="bento-victim">
            <img src={JUSTICE_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 transition-opacity" />
            <div className="relative">
              <div className="text-gold text-xs font-mono tracking-widest">VICTIM SUPPORT</div>
              <div className="font-display text-4xl mt-3 max-w-md">You are not alone. Help is available.</div>
              <div className="mt-4 text-white/70 max-w-md">Emergency contacts, legal aid, counselling, women&apos;s &amp; child protection resources — worldwide.</div>
              <Button className="mt-6 rounded-full bg-gold text-black hover:bg-gold-light">Find Help Near You <HeartHandshake className="ml-2 h-4 w-4" /></Button>
            </div>
          </Link>
          <Link to="/awareness" className="rounded-2xl border border-white/5 bg-obsidian-soft p-8 group hover:border-gold/40 transition-all" data-testid="bento-awareness">
            <Megaphone className="h-8 w-8 text-gold" />
            <div className="font-display text-2xl mt-6">Crime Awareness Campaigns</div>
            <div className="mt-2 text-white/60 text-sm">Deepfake alerts, phishing tips, prevention posters and quizzes.</div>
          </Link>
          <Link to="/volunteer" className="rounded-2xl border border-white/5 bg-obsidian-soft p-8 group hover:border-gold/40 transition-all" data-testid="bento-volunteer">
            <Users className="h-8 w-8 text-gold" />
            <div className="font-display text-2xl mt-6">Volunteer</div>
            <div className="mt-2 text-white/60 text-sm">Lawyers, researchers, students, developers — join a global mission.</div>
          </Link>
          <Link to="/research" className="md:col-span-2 rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-8 group" data-testid="bento-research">
            <Trophy className="h-8 w-8 text-gold" />
            <div className="font-display text-3xl mt-6">Independent research, open to the world.</div>
            <div className="mt-2 text-white/70 text-sm">Peer-reviewed papers, annual reports and open datasets from our research units.</div>
            <Button className="mt-6 rounded-full bg-gold text-black hover:bg-gold-light">Explore Research</Button>
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="home-testimonials">
        <SectionTitle eyebrow="TESTIMONIALS" title="Trusted by researchers, journalists, and law enforcement." />
        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {TESTIMONIALS.map((t) => (
            <Card key={t.name} className="bg-obsidian-soft border-white/5 rounded-2xl">
              <CardContent className="p-6">
                <div className="text-gold text-3xl font-display">&ldquo;</div>
                <div className="text-white/80 leading-relaxed">{t.quote}</div>
                <div className="mt-6 border-t border-white/5 pt-4">
                  <div className="text-white font-medium">{t.name}</div>
                  <div className="text-white/50 text-xs">{t.role}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CAMPAIGNS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="home-campaigns">
        <SectionTitle eyebrow="AWARENESS CAMPAIGNS" title="Public education, at global scale." />
        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {CAMPAIGNS.map((c) => (
            <div key={c.title} className={`rounded-2xl border border-white/5 bg-gradient-to-br ${c.color} to-transparent p-8 min-h-[220px] flex flex-col justify-between`}>
              <div className="text-xs font-mono tracking-widest text-gold">{c.tag.toUpperCase()}</div>
              <div className="font-display text-3xl text-white">{c.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PARTNERS */}
      <section className="border-y border-white/5 py-10" data-testid="home-partners">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-8 overflow-hidden">
          <div className="text-xs font-mono tracking-widest text-gold shrink-0">PARTNERS</div>
          <div className="flex gap-16 marquee whitespace-nowrap">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <span key={i} className="font-display text-2xl text-white/40 hover:text-gold transition-colors">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="home-newsletter">
        <SectionTitle align="center" eyebrow="THE IOCRI BRIEFING" title="A weekly intelligence briefing, in your inbox." subtitle="Signal — not noise. Curated updates on global organized crime, AI safety, and public policy." />
        <form onSubmit={submitNewsletter} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="home-newsletter-form">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="home-newsletter-input" placeholder="Enter your email" className="bg-black border-white/10 rounded-full h-12 px-5 text-white" />
          <Button type="submit" data-testid="home-newsletter-submit" className="rounded-full bg-gold text-black hover:bg-gold-light h-12 px-7">Subscribe</Button>
        </form>
      </section>
    </div>
  );
}
